import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import styles from '../components/enquiries/Enquiries.module.css';
import { getCommunityGroup } from '../data/community';

const CommunityGroupPage = () => {
  const { groupSlug } = useParams();
  const group = getCommunityGroup(groupSlug);
  const items = Array.isArray(group?.items) ? group.items : [];

  if (!group) {
    return <Navigate to="/community" replace />;
  }

  const GroupIcon = group.icon;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-m)'
          }}
        >
          <Link
            to="/community"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-xxs)',
              color: 'var(--Text-Secondary)',
              textDecoration: 'none',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            <ChevronLeftIcon style={{ width: '16px', height: '16px' }} />
            <span>Back to Community</span>
          </Link>

          <div className={styles.headerContent}>
            <div
              className={styles.headerText}
              style={{
                position: 'relative',
                paddingRight: '88px'
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: 'var(--radius-m)',
                  background: group.accent.background,
                  border: `1px solid ${group.accent.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: 0,
                  right: 0
                }}
              >
                <GroupIcon
                  style={{
                    width: '26px',
                    height: '26px',
                    color: group.accent.foreground
                  }}
                />
              </div>

              <h1
                className={styles.headerTitle}
                style={{ marginRight: 'var(--spacing-m)' }}
              >
                {group.title}
              </h1>
              <p className={styles.headerSubtitle}>{group.heroDescription}</p>
            </div>
          </div>
        </div>
      </div>

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'var(--spacing-l)'
        }}
      >
        {items.map((item) => {
          const isEmpty = item.type === 'empty';
          const isExternal = item.type === 'external';

          const action = isEmpty ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--spacing-xs) var(--spacing-m)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--Grey-100)',
                color: 'var(--Text-Secondary)',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)'
              }}
            >
              {item.cta}
            </span>
          ) : isExternal ? (
            <a
              href={item.path}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-xxs)',
                color: group.accent.foreground,
                textDecoration: 'none',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)'
              }}
            >
              <span>{item.cta}</span>
            </a>
          ) : (
            <Link
              to={item.path}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-xxs)',
                color: group.accent.foreground,
                textDecoration: 'none',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)'
              }}
            >
              <span>{item.cta}</span>
            </Link>
          );

          const card = (
            <article
              style={{
                background: 'var(--Surface-Primary)',
                border: 'var(--card-border)',
                borderRadius: 'var(--radius-xl)',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-card)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                minHeight: '100%',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isEmpty) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-frame-big)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isEmpty) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-card)';
                }
              }}
            >
              <div
                style={{
                  height: '220px',
                  background: group.accent.background
                }}
              >
                <img
                  src={item.image}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </div>

              <div
                style={{
                  flex: 1,
                  padding: 'var(--spacing-xl)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-l)'
                }}
              >
                <div>
                  <h2
                    style={{
                      margin: '0 0 var(--spacing-xs) 0',
                      color: 'var(--Text-Primary)',
                      fontSize: 'var(--font-size-subtitle2)',
                      fontWeight: 'var(--font-weight-subtitle2)'
                    }}
                  >
                    {item.title}
                  </h2>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--Text-Secondary)',
                      fontSize: 'var(--font-size-body3)',
                      lineHeight: '1.6'
                    }}
                  >
                    {item.description}
                  </p>
                </div>

                <div style={{ marginTop: 'auto' }}>
                  {action}
                </div>
              </div>
            </article>
          );

          if (isEmpty) {
            return (
              <div key={item.title}>
                {card}
              </div>
            );
          }

          if (isExternal) {
            return (
              <a
                key={item.title}
                href={item.path}
                target="_blank"
                rel="noreferrer"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                  minHeight: '100%'
                }}
              >
                {card}
              </a>
            );
          }

          return (
            <Link
              key={item.title}
              to={item.path}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                minHeight: '100%'
              }}
            >
              {card}
            </Link>
          );
        })}
      </section>

      <Link
        to="/contact-support"
        style={{
          display: 'block',
          marginTop: 'var(--spacing-l)',
          textDecoration: 'none',
          color: 'inherit'
        }}
      >
        <article
          style={{
            background: 'var(--Surface-Primary)',
            border: 'var(--card-border)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xl)',
            boxShadow: 'var(--shadow-card)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = 'var(--shadow-frame-big)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'var(--shadow-card)';
          }}
        >
          <h2
            style={{
              margin: '0 0 var(--spacing-xs) 0',
              color: 'var(--Text-Primary)',
              fontSize: 'var(--font-size-subtitle2)',
              fontWeight: 'var(--font-weight-subtitle2)'
            }}
          >
            Want to contribute in another way?
          </h2>
          <p
            style={{
              margin: '0 0 var(--spacing-m) 0',
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body3)',
              lineHeight: '1.6'
            }}
          >
            If what you’re looking for is not listed on this page, let us know what you have in mind and we’ll help direct it properly.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)',
              color: group.accent.foreground,
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            <span>Get in touch</span>
            <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
          </div>
        </article>
      </Link>
    </div>
  );
};

export default CommunityGroupPage;
